import { ApolloError, ApolloLink, FetchResult, NextLink, Operation, ServerError } from '@apollo/client/core';
import { SeverityLevel } from '@sentry/types';
import Observable from 'zen-observable';

import { GraphQLBreadcrumb } from 'apollo-link-sentry';
import { SentryLinkOptions, withDefaults, type FullOptions } from 'apollo-link-sentry/lib-esm/options';
import { makeBreadcrumb } from 'apollo-link-sentry/lib-esm/breadcrumb';
import { attachBreadcrumbToSentry, setFingerprint, setTransaction } from 'apollo-link-sentry/lib-esm/sentry';
import { getMainDefinition } from '@apollo/client/utilities';

const isServerError = (error: unknown): error is ServerError =>
  typeof error === 'object' && error !== null && 'response' in error && 'result' in error && 'statusCode' in error;

const severityForResult = (result: FetchResult): SeverityLevel =>
  result.errors && result.errors.length > 0 ? 'error' : 'info';

/**
 * Adapted from https://github.com/DiederikvandenB/apollo-link-sentry/blob/8b90e7a1abe88ae66126aa4971ed0da4ae16b190/src/SentryLink.ts
 *
 * Includes two changes:
 * - add errors to breadcrumb regardless of error count, see https://github.com/DiederikvandenB/apollo-link-sentry/pull/410#discussion_r771429756
 * - add data results from subscriptions immediately, because complete is only called when the subscription is ended
 */
export class SentrySubscriptionLink extends ApolloLink {
  private readonly options: FullOptions;

  constructor(options: SentryLinkOptions = {}) {
    super();
    this.options = withDefaults(options);
  }

  request(operation: Operation, forward: NextLink): Observable<FetchResult> | null {
    if (typeof this.options.shouldHandleOperation === 'function') {
      if (!this.options.shouldHandleOperation(operation)) {
        return forward(operation);
      }
    }

    if (this.options.setTransaction) {
      setTransaction(operation);
    }

    const breadcrumb = this.options.attachBreadcrumbs ? makeBreadcrumb(operation, this.options) : undefined;

    const mainDefinition = getMainDefinition(operation.query);
    const isSubscription = mainDefinition.kind === 'OperationDefinition' && mainDefinition.operation === 'subscription';

    // While this could be done more simplistically by simply subscribing,
    // wrapping the observer in our own observer ensures we get the results
    // before they are passed along to other observers. This guarantees we
    // get to run our instrumentation before others observers potentially
    // throw and thus flush the results to Sentry.
    return new Observable<FetchResult>((originalObserver) => {
      const subscription = forward(operation).subscribe({
        next: (result) => {
          if (this.options.attachBreadcrumbs) {
            // We must have a breadcrumb if attachBreadcrumbs was set
            (breadcrumb as GraphQLBreadcrumb).level = severityForResult(result);

            if (this.options.attachBreadcrumbs.includeFetchResult) {
              // We must have a breadcrumb if attachBreadcrumbs was set
              (breadcrumb as GraphQLBreadcrumb).data.fetchResult = result;
            }

            if (this.options.attachBreadcrumbs.includeError && result.errors) {
              // We must have a breadcrumb if attachBreadcrumbs was set
              (breadcrumb as GraphQLBreadcrumb).data.error = new ApolloError({
                graphQLErrors: result.errors,
              });
            }

            if (isSubscription) {
              // don't wait for complete, which is only fired when the subcription ends
              attachBreadcrumbToSentry(
                operation,
                // We must have a breadcrumb if attachBreadcrumbs was set
                breadcrumb as GraphQLBreadcrumb,
                this.options
              );
            }
          }

          originalObserver.next(result);
        },
        complete: () => {
          if (this.options.attachBreadcrumbs && !isSubscription) {
            attachBreadcrumbToSentry(
              operation,
              // We must have a breadcrumb if attachBreadcrumbs was set
              breadcrumb as GraphQLBreadcrumb,
              this.options
            );
          }

          originalObserver.complete();
        },
        error: (error) => {
          if (this.options.setFingerprint) {
            // set fingerprint to group events
            // if a request failed, it's likely that whatever errors comes next is because of it
            setFingerprint(operation);
          }

          if (this.options.attachBreadcrumbs) {
            // We must have a breadcrumb if attachBreadcrumbs was set
            (breadcrumb as GraphQLBreadcrumb).level = 'error';

            // eslint-disable-next-line @typescript-eslint/init-declarations
            let scrubbedError: any;
            if (isServerError(error)) {
              const { result, response: _response, ...rest } = error;
              scrubbedError = rest;

              if (this.options.attachBreadcrumbs.includeFetchResult) {
                // We must have a breadcrumb if attachBreadcrumbs was set
                (breadcrumb as GraphQLBreadcrumb).data.fetchResult = result;
              }
            } else {
              scrubbedError = error;
            }

            if (this.options.attachBreadcrumbs.includeError) {
              // We must have a breadcrumb if attachBreadcrumbs was set
              (breadcrumb as GraphQLBreadcrumb).data.error = scrubbedError;
            }

            attachBreadcrumbToSentry(
              operation,
              // We must have a breadcrumb if attachBreadcrumbs was set
              breadcrumb as GraphQLBreadcrumb,
              this.options
            );
          }

          originalObserver.error(error);
        },
      });

      return () => {
        subscription.unsubscribe();
      };
    });
  }
}
